import type { ViewRect } from "./view-chunks.ts";

/**
 * Which animated cells a camera needs to drive (P1.6-T6): the tiles the camera's own view touches plus a small
 * margin, not the chunk range `visibleChunks` keeps painted, which carries a whole chunk on every side. The range is
 * in whole tiles so a caller rescans its cells only when a view edge crosses a tile boundary.
 */

/**
 * Tiles of margin kept beyond the view on every side. A frame of panning at the editor's top speed moves the view
 * well under one tile, and the view is read from the camera's scroll rather than its last rendered rectangle, so
 * two tiles leave a cell animating before any part of it can be seen.
 */
export const ANIMATED_CELL_MARGIN_TILES = 2;

/**
 * An inclusive range of world tile columns and rows. It is not clamped to a map, since a cell outside the map does
 * not exist to be tested.
 */
export interface TileRange {
  readonly minTx: number;
  readonly minTy: number;
  readonly maxTx: number;
  readonly maxTy: number;
}

/**
 * A tile range a caller keeps and has filled in place, so asking every frame allocates nothing.
 */
export interface TileRangeOut {
  minTx: number;
  minTy: number;
  maxTx: number;
  maxTy: number;
}

/**
 * The world rectangle a camera shows at its current scroll and zoom. A camera's scroll names the point at the middle
 * of its viewport less half the viewport at zoom 1, whatever the zoom, so the view is centred on that midpoint.
 *
 * @param scrollX The camera's horizontal scroll.
 * @param scrollY The camera's vertical scroll.
 * @param width The viewport's width in screen pixels.
 * @param height The viewport's height in screen pixels.
 * @param zoom The camera's zoom, above zero.
 * @returns The view in world pixels.
 */
export function cameraView(scrollX: number, scrollY: number, width: number, height: number, zoom: number): ViewRect {
  const displayWidth = width / zoom;
  const displayHeight = height / zoom;
  return { left: scrollX + width / 2 - displayWidth / 2, top: scrollY + height / 2 - displayHeight / 2, width: displayWidth, height: displayHeight };
}

/**
 * The tiles a view touches plus a margin. The last tile a view edge touches is `ceil(edge / tileSize) - 1`, so an edge
 * on a tile boundary takes no tile beyond it.
 *
 * @param view View rectangle in world pixels.
 * @param tileSize Tile size in pixels.
 * @param marginTiles Tiles of margin on every side.
 * @param out A range to fill and return in place of a new one, for a caller asking every frame.
 * @returns The inclusive tile range.
 */
export function animatedTileRange(view: ViewRect, tileSize: number, marginTiles: number, out?: TileRangeOut): TileRange {
  const range = out ?? { minTx: 0, minTy: 0, maxTx: 0, maxTy: 0 };
  range.minTx = Math.floor(view.left / tileSize) - marginTiles;
  range.minTy = Math.floor(view.top / tileSize) - marginTiles;
  range.maxTx = Math.ceil((view.left + view.width) / tileSize) - 1 + marginTiles;
  range.maxTy = Math.ceil((view.top + view.height) / tileSize) - 1 + marginTiles;
  return range;
}

/**
 * Whether a cell's frame reaches into a tile range. A frame wider or taller than a tile covers the tiles to its right
 * and below its own, so it is in the range while any of them is.
 *
 * @param range The tile range.
 * @param tx The cell's tile column.
 * @param ty The cell's tile row.
 * @param spanX Tile columns the frame covers, at least 1.
 * @param spanY Tile rows the frame covers, at least 1.
 * @returns True when any covered tile is in the range.
 */
export function boxInTileRange(range: TileRange, tx: number, ty: number, spanX: number, spanY: number): boolean {
  return tx + spanX - 1 >= range.minTx && tx <= range.maxTx && ty + spanY - 1 >= range.minTy && ty <= range.maxTy;
}
